"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface TableOfContentsProps {
  toc?: {
    level: number;
    text: string;
    slug: string;
  }[];
}

export function TableOfContents({ toc }: TableOfContentsProps) {
  const [activeHeading, setActiveHeading] = useState("");
  const headingElementsRef = useRef<{
    [key: string]: IntersectionObserverEntry;
  }>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("id")!;
          headingElementsRef.current[id] = entry;
        });

        const visibleHeadings: IntersectionObserverEntry[] = [];
        Object.keys(headingElementsRef.current).forEach((key) => {
          const entry = headingElementsRef.current[key];
          if (entry.isIntersecting) visibleHeadings.push(entry);
        });

        if (visibleHeadings.length > 0) {
          setActiveHeading(visibleHeadings[0].target.getAttribute("id") || "");
        }
      },
      { rootMargin: "0px 0px -80% 0px" },
    );

    const elements = document.querySelectorAll("h2, h3");
    elements.forEach((elem) => observer.observe(elem));

    return () => observer.disconnect();
  }, []);

  if (!toc?.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="font-medium">On This Page</p>
      <div className="space-y-2">
        {toc.map((heading) => (
          <a
            key={heading.slug}
            href={`#${heading.slug}`}
            className={cn(
              "block text-sm transition-colors hover:text-foreground",
              {
                "text-muted-foreground": activeHeading !== heading.slug,
                "font-medium text-foreground": activeHeading === heading.slug,
                "pl-4": heading.level === 3,
              },
            )}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector(`#${heading.slug}`)?.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
              });
            }}
          >
            {heading.text}
          </a>
        ))}
      </div>
    </div>
  );
}
